import { registerActions } from "../actionRegistry";
/**
 * Miscellaneous Operations Functions
 *
 * Various utility and miscellaneous functions for the legacy app.
 */

import { api } from "../api";
import {
  reordermode,
  setReordermode,
  load_active,
  loadmoretimer,
  setLoadmoretimer,
  setReorderSelectedCount,
} from "../globalState";

import { toastSuccess, toastError } from "../ui/messages";
import { refreshModal, urlUpdatePublic } from "../ui/modal";
import { load } from "../ui/contentLoader";
import { savecollectionorder } from "./collections";
import { view, search as navigationSearch } from "./navigation";
import { directoryRefresh } from "./directoryUpload";
import {
  getDomElement,
  getDomElementOrThrow,
  setText,
  setStyle,
  queryAll,
  idSuffix,
  fadeOut,
} from "../utils/dom";

/** Apply one inline style property to every element matching `selector`. */
function setStyleAll(selector: string, prop: string, value: string): void {
  for (const el of queryAll<HTMLElement>(selector)) el.style.setProperty(prop, value);
}

/**
 * Paint a sidebar filter option as on/off: the segment's disabled class plus the
 * checkbox/radio glyph swap.
 */
function setOptionState(option_id: string, on: boolean, offIcon: string, onIcon: string): void {
  getDomElement("option-segment-" + option_id)?.classList.toggle(
    "window-sidebar-options-option-disabled",
    !on
  );
  const icon = getDomElement("option-icon-" + option_id);
  if (!icon) return;
  const [addCls, removeCls] = on ? [onIcon, offIcon] : [offIcon, onIcon];
  icon.classList.add(...addCls.split(" "));
  icon.classList.remove(...removeCls.split(" "));
}
import { makeSortable, destroySortable } from "../plugins/sortable";
import { printSizeSegment } from "./printSizeFilter";

/**
 * Toggle reorder mode for collection items
 */
export function reorder(): void {
  if (reordermode !== 0) {
    setReordermode(0);
    setText("reorderbutton", "Re-order");
    destroySortable("collection-target");
    void savecollectionorder();
    setStyleAll(".already-downloaded", "display", "");
    setStyleAll(".in-list", "display", "");
    return;
  }

  // Disable infinite scroll so it doesn't interfere with reorder loading
  const infScrollEl = getDomElement<HTMLInputElement>("lm_infscroll_enabled");
  if (infScrollEl) infScrollEl.value = "0";

  // Check if there is still collection content to load
  // lm_active=1 means there's more to fetch, but only when infinite scroll
  // hasn't already taken over (lm_infscroll_active=1 means we're past collection content)
  let needload = 0;
  const infScrollActiveEl = getDomElement<HTMLInputElement>("lm_infscroll_active");
  const alreadyInInfScroll = infScrollActiveEl?.value === "1";

  if (!alreadyInInfScroll && load_active === 0) {
    const lmMode = getDomElement("lm_mode");
    const lmTarget = getDomElement<HTMLInputElement>("lm_target");
    if (lmMode && lmTarget && getDomElement(lmTarget.value)) {
      if (getDomElement<HTMLInputElement>("lm_active")?.value === "1") {
        needload = 1;
      }
    }
  }

  if (needload === 1) {
    toastSuccess("Hold on, loading all artworks...");
    clearTimeout(loadmoretimer);
    load();
    setLoadmoretimer(setTimeout(reorder, 1000));
    return;
  }

  setReordermode(1);
  setText("reorderbutton", "Save order");
  makeSortable("collection-target");
  setReorderSelectedCount(0);
  setTimeout(reorderpostload, 1000);
}

/**
 * Post-load setup for reorder mode
 */
export function reorderpostload(): void {
  view("tinythumbs");

  setStyleAll(".reorder-select", "display", "inline");

  setStyleAll(".already-downloaded", "display", "none");
  setStyleAll(".in-list", "display", "none");
}

export async function optinartwork(id: string): Promise<void> {
  if (!confirm("Do you want to opt-in with artwork ID #" + id + "?")) return;
  try {
    await api.artwork.optin({ id: Number(id) });
    await directoryRefresh(id);
    toastSuccess("Artwork opted-in for expanded products");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function optinartworkedit(id: string): Promise<void> {
  try {
    await api.artwork.optin({ id: Number(id) });
    await directoryRefresh(id);
    refreshModal();
    toastSuccess("Artwork opted-in for expanded products");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

/**
 * Parse and apply filter options
 */
export function parseOptions(): void {
  const singlefilter: Record<string, string> = {};
  let url_options = "";
  let all_options = "";
  let filters_active = 0;

  function addOption(optionId: string) {
    filters_active = 1;
    if (url_options !== "") url_options += "/";
    url_options += optionId;
    if (all_options !== "") all_options += ",";
    all_options += optionId;
  }

  for (const optEl of queryAll<HTMLElement>(".all-options")) {
    const option_id = idSuffix(optEl, "option-");
    if (!option_id) continue;

    const filterIdEl = getDomElement<HTMLInputElement>("filter-id-" + option_id);
    if (!filterIdEl) continue;
    const filter_id = filterIdEl.value;

    const filterTypeEl = getDomElement<HTMLInputElement>("filter-type-" + filter_id);
    if (!filterTypeEl) continue;
    const filter_type = filterTypeEl.value;

    const optionEl = getDomElement<HTMLInputElement>("option-" + option_id);
    if (!optionEl) continue;
    const filter_var = optionEl.value;

    if (filter_type === "select-menu") {
      if (filter_var === "1") addOption(option_id);
      continue;
    }

    if (filter_type !== "multi-select" && filter_type !== "single-select") continue;

    // "off" and "on" glyph pair for this filter's control type.
    const [offIcon, onIcon] =
      filter_type === "multi-select"
        ? (["far fa-square", "fas fa-check-square"] as const)
        : (["far fa-circle", "fas fa-dot-circle"] as const);

    if (filter_var !== "1") {
      setOptionState(option_id, false, offIcon, onIcon);
      continue;
    }

    filters_active = 1;

    if (filter_type === "multi-select") {
      setOptionState(option_id, true, offIcon, onIcon);
      addOption(option_id);
      continue;
    }

    // single-select
    if (!singlefilter[filter_id]) {
      setOptionState(option_id, true, offIcon, onIcon);
      singlefilter[filter_id] = option_id;
      addOption(option_id);
    } else {
      optionEl.value = "0";
      setOptionState(option_id, false, offIcon, onIcon);
    }
  }

  // "Minimum DPI/PPI" rides in the URL like every other filter, as one segment
  // (`min-dpi-300-70x100cm`) rather than four. It is NOT added to `all_options`: that list
  // is the set of option ids the server toggles on, and this filter travels as its own
  // `printsize` value in the loader payload. It does count as an active filter, so the
  // Clear button lights up and clearing it removes the segment.
  const dpiSegment = printSizeSegment();
  if (dpiSegment !== "") {
    filters_active = 1;
    url_options = url_options === "" ? dpiSegment : url_options + "/" + dpiSegment;
  }

  if (getDomElement("lm_url") !== null) {
    if (getDomElement("front-search") !== null) {
      getDomElementOrThrow<HTMLInputElement>("lm_url").value =
        "/search/" + getDomElementOrThrow<HTMLInputElement>("front-search").value;
    }
    let new_url = getDomElementOrThrow<HTMLInputElement>("lm_url").value;
    if (url_options !== "") {
      new_url += "/" + url_options;
    }
    urlUpdatePublic(new_url, true);
  }

  setStyle("clearfilters", "opacity", filters_active === 1 ? "1" : "0.3");
  getDomElementOrThrow<HTMLInputElement>("lm_options").value = all_options;

  if (getDomElement("front-search") !== null) {
    navigationSearch();
  } else {
    getDomElementOrThrow<HTMLInputElement>("lm_active").value = "1";
    getDomElementOrThrow<HTMLInputElement>("lm_from").value = "0";
    setText(getDomElementOrThrow<HTMLInputElement>("lm_target").value, "");

    const jsonObj: Record<string, string>[] = [];
    const item: Record<string, string> = {};

    if (getDomElement("lightbox_id") !== null) {
      item.lightbox_id = getDomElementOrThrow<HTMLInputElement>("lightbox_id").value;
    }
    if (getDomElement("featuredtag_id") !== null) {
      item.featuredtag_id = getDomElementOrThrow<HTMLInputElement>("featuredtag_id").value;
    }
    if (getDomElement("collection_id") !== null) {
      item.collection_id = getDomElementOrThrow<HTMLInputElement>("collection_id").value;
    }
    if (getDomElement("artist_id") !== null) {
      item.artist_id = getDomElementOrThrow<HTMLInputElement>("artist_id").value;
    }
    item.options = getDomElementOrThrow<HTMLInputElement>("lm_options").value;

    jsonObj.push(item);
    const jsonString = JSON.stringify(jsonObj);
    getDomElementOrThrow<HTMLInputElement>("lm_filters").value = jsonString;

    load();
  }
}

/**
 * Close new feature notification
 */
export function newfeatureclose(feature: string): void {
  api.features
    .close({ feature })
    .then(() => {
      fadeOut(`new-feature-${feature}`);
    })
    .catch((err: unknown) => {
      toastError((err as Error).message);
    });
}

export async function clearQd(_id: string): Promise<void> {
  try {
    await api.quickDownload.clear();
    toastSuccess("QD cleared");
    const qdEl = document.getElementById("quick-downloader");
    if (qdEl) qdEl.style.display = "none";
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

registerActions({
  reorder,
  reorderpostload,
  newfeatureclose,
  clearQd,
});
